import type { SupabaseClient } from "@supabase/supabase-js";
import type { MasteryState } from "../mastery/masteryState.js";
import type { PlayerState } from "../session.js";
import type { Question } from "../types.js";
import type { AttemptRecord, EnginePersistence } from "./types.js";

export interface SupabasePersistenceOptions {
  client: SupabaseClient;
  userId: string;
  subjectId: string;
}

const DEFAULT_MASTERY: MasteryState = { defaultRating: 1200, byConcept: {} };

/**
 * Postgres-backed `EnginePersistence` for Supabase (D.0.4).
 *
 * One instance per `(user, subject)`. RLS gates rows to `auth.uid() = user_id`,
 * so the underlying client should be authenticated as `userId` (or use the
 * service-role key for server / test contexts).
 *
 * Question bank persistence is intentionally a no-op: the PWA regenerates the
 * generated bank deterministically from `(seed, locale)`, so storing it
 * server-side would just serialize a cache.
 *
 * Not exported from `src/index.ts`. Import via the dedicated subpath:
 *
 * ```ts
 * import { SupabasePersistence } from "notanyai/persistence/supabase";
 * ```
 */
export class SupabasePersistence implements EnginePersistence {
  constructor(private readonly opts: SupabasePersistenceOptions) {}

  async loadPlayer(): Promise<PlayerState | null> {
    const { client, userId, subjectId } = this.opts;
    const { data, error } = await client
      .from("player_state")
      .select("rating, games_played")
      .eq("user_id", userId)
      .eq("subject_id", subjectId)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return { rating: data.rating, gamesPlayed: data.games_played };
  }

  async savePlayer(state: PlayerState): Promise<void> {
    const { client, userId, subjectId } = this.opts;
    const { error } = await client.from("player_state").upsert(
      {
        user_id: userId,
        subject_id: subjectId,
        // Round to integer: Elo calculations produce floats, but the Postgres
        // column is `integer not null` and PostgREST sends JSON values as-is,
        // so "1247.593" would fail with "invalid input syntax for type integer".
        rating: Math.round(state.rating),
        games_played: state.gamesPlayed,
      },
      { onConflict: "user_id,subject_id" }
    );
    if (error) throw error;
  }

  loadQuestionBank(): Question[] | null {
    return null;
  }

  saveQuestionBank(_questions: Question[]): void {
    // intentional no-op; see class docstring.
  }

  async appendAttempt(record: AttemptRecord): Promise<void> {
    const { client, userId, subjectId } = this.opts;
    const { error } = await client.from("attempts").insert({
      user_id: userId,
      subject_id: subjectId,
      at: record.at,
      question_id: record.questionId,
      concept_id: record.conceptId,
      correct: record.correct,
      player_rating_after: Math.round(record.playerRatingAfter),
      weight: record.weight ?? 1,
      game_type: record.gameType ?? "mcq",
    });
    if (error) throw error;
  }

  async loadAttempts(limit?: number): Promise<AttemptRecord[]> {
    const { client, userId, subjectId } = this.opts;
    let q = client
      .from("attempts")
      .select("at, question_id, concept_id, correct, player_rating_after, weight, game_type")
      .eq("user_id", userId)
      .eq("subject_id", subjectId)
      .order("at", { ascending: false });
    if (typeof limit === "number") q = q.limit(limit);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map((r) => ({
      at: r.at,
      questionId: r.question_id,
      conceptId: r.concept_id,
      correct: r.correct,
      playerRatingAfter: r.player_rating_after,
      weight: r.weight ?? undefined,
      gameType: r.game_type ?? undefined,
    }));
  }

  async loadMastery(): Promise<MasteryState | null> {
    const { client, userId, subjectId } = this.opts;
    const { data, error } = await client
      .from("player_state")
      .select("mastery")
      .eq("user_id", userId)
      .eq("subject_id", subjectId)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return (data.mastery as MasteryState | null) ?? DEFAULT_MASTERY;
  }

  async saveMastery(state: MasteryState): Promise<void> {
    const { client, userId, subjectId } = this.opts;
    const { error } = await client
      .from("player_state")
      .upsert(
        { user_id: userId, subject_id: subjectId, mastery: state },
        { onConflict: "user_id,subject_id" }
      );
    if (error) throw error;
  }
}
